/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, ActionMenu, Button, Modal } from '../../design-system';
import { Input, message, Space } from 'antd';
import { PlusOutlined, SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import DepartmentFormModal from './DepartmentFormModal';
import { departmentService } from '../../services/departmentService';
import { getApiErrorMessage } from '../../utils/errorUtils';

const getDepartmentOwnerName = (department) =>
    department.leader?.full_name || department.pseudo_leader?.full_name || '';

const renderSetupPill = (label, value, tone = 'neutral') => {
    const colors = {
        good: { background: '#f6ffed', border: '#b7eb8f', color: '#237804' },
        warning: { background: '#fffbe6', border: '#ffe58f', color: '#8a6d00' },
        neutral: { background: '#f5f5f5', border: '#d9d9d9', color: '#595959' }
    };
    const palette = colors[tone] || colors.neutral;

    return (
        <div
            style={{
                border: `1px solid ${palette.border}`,
                background: palette.background,
                color: palette.color,
                borderRadius: 8,
                padding: '8px 10px',
                minWidth: 150
            }}
        >
            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
        </div>
    );
};

const DepartmentList = () => {
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDepartment, setEditingDepartment] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadDepartments();
    }, [pagination.current, search, sortBy, sortOrder]);

    const loadDepartments = async () => {
        setLoading(true);
        try {
            const result = await departmentService.getDepartments({
                page: pagination.current,
                limit: pagination.pageSize,
                search,
                sortBy,
                sortOrder
            });

            setDepartments(result.departments || []);
            setPagination({ ...pagination, total: result.total || 0 });
        } catch (error) {
            message.error('Failed to load departments');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingDepartment(null);
        setModalVisible(true);
    };

    const handleEdit = (department) => {
        setEditingDepartment(department);
        setModalVisible(true);
    };

    const handleDelete = (departmentId, departmentName, memberCount) => {
        if (memberCount > 0) {
            message.error('Cannot delete department with organization members');
            return;
        }

        Modal.confirm({
            title: 'Delete Department',
            content: `Are you sure you want to delete "${departmentName}"?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await departmentService.deleteDepartment(departmentId);
                    message.success('Department deleted successfully');
                    loadDepartments();
                } catch (error) {
                    if (getApiErrorMessage(error, "").includes('must_empty')) {
                        message.error('Cannot delete department with organization members');
                    } else {
                        message.error('Failed to delete department');
                    }
                }
            }
        });
    };

    const handleModalClose = (refresh = false) => {
        setModalVisible(false);
        if (refresh) {
            loadDepartments();
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPagination({ ...pagination, current: 1 }); // Reset to first page
    };

    const setupSummary = useMemo(() => {
        const visibleDepartments = departments || [];
        const withMembers = visibleDepartments.filter(dept => Number(dept.active_members_count || 0) > 0);
        const missingLeader = visibleDepartments.filter(dept => !getDepartmentOwnerName(dept));
        const missingEmail = visibleDepartments.filter(dept => !String(dept.email || '').trim());
        const missingSubLeads = withMembers.filter(dept => !dept.sub_leaders?.length);
        const completedSignals = [
            missingLeader.length === 0,
            missingEmail.length === 0,
            missingSubLeads.length === 0
        ].filter(Boolean).length;

        return {
            total: visibleDepartments.length,
            activeMembers: visibleDepartments.reduce((sum, dept) => sum + Number(dept.active_members_count || 0), 0),
            missingLeader,
            missingEmail,
            missingSubLeads,
            score: Math.round((completedSignals / 3) * 100)
        };
    }, [departments]);

    const renderDepartmentSetup = () => {
        if (!setupSummary.total) return null;

        const nextItems = [
            setupSummary.missingLeader.length > 0 ? `${setupSummary.missingLeader.length} missing leader/director` : null,
            setupSummary.missingEmail.length > 0 ? `${setupSummary.missingEmail.length} missing contact email` : null,
            setupSummary.missingSubLeads.length > 0 ? `${setupSummary.missingSubLeads.length} missing sub-leads` : null
        ].filter(Boolean);

        return (
            <div
                style={{
                    marginBottom: 16,
                    padding: '14px 16px',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    background: '#fff'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 260, flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>Department setup</div>
                        <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 3 }}>
                            Add owners and contact details so each team knows who should maintain its rate card.
                        </div>
                        <div style={{ marginTop: 10, height: 6, background: '#f0f0f0', borderRadius: 999, overflow: 'hidden' }}>
                            <div
                                style={{
                                    width: `${setupSummary.score}%`,
                                    height: '100%',
                                    background: setupSummary.score === 100 ? '#52c41a' : '#f6c343'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
                        {renderSetupPill('Departments', setupSummary.total)}
                        {renderSetupPill('Active members', setupSummary.activeMembers)}
                        {renderSetupPill(
                            'Needs attention',
                            nextItems.length || 'None',
                            nextItems.length ? 'warning' : 'good'
                        )}
                    </div>
                </div>

                {nextItems.length > 0 ? (
                    <div style={{ marginTop: 10, color: '#595959', fontSize: 13 }}>
                        Next: {nextItems.join(' · ')}
                    </div>
                ) : null}
            </div>
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            sorter: true,
            sortOrder: sortBy === 'description' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null
        },
        {
            title: 'Contact Email',
            dataIndex: 'email',
            key: 'email',
            render: (email) => email || <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>—</span>
        },
        {
            title: 'Leader/Director',
            dataIndex: 'leader',
            key: 'leader',
            render: (leader, record) => {
                // Priority 1: Actual leader (normal text)
                if (leader?.full_name) {
                    return leader.full_name;
                }
                // Priority 2: Pseudo leader (grey italic - fallback)
                if (record.pseudo_leader?.full_name) {
                    return (
                        <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                            {record.pseudo_leader.full_name}
                        </span>
                    );
                }
                // Priority 3: No leader
                return '-';
            }
        },
        {
            title: 'Sub-Leads',
            dataIndex: 'sub_leaders',
            key: 'sub_leaders',
            render: (subLeaders) => subLeaders?.map(sl => sl.full_name).join(', ') || '-'
        },
        {
            title: 'Active Members',
            dataIndex: 'active_members_count',
            key: 'active_members_count',
            render: (count) => count || 0
        },
        {
            title: 'Job Listings',
            dataIndex: 'jobs_count',
            key: 'jobs_count',
            render: (count) => count || 0
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <ActionMenu
                    items={['edit', 'delete']}
                    record={record}
                    onAction={(key, record) => {
                        if (key === 'edit') {
                            handleEdit(record);
                        } else if (key === 'delete') {
                            handleDelete(record.id, record.name, record.active_members_count);
                        }
                    }}
                    disabledKeys={record.active_members_count > 0 ? ['delete'] : []}
                />
            )
        }
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setPagination(pagination);
        if (sorter.field) {
            setSortBy(sorter.field);
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    };

    return (
        <div>
            {/* Header with Add Button and Search */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontWeight: 700 }}>Departments</div>
                    <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                        Keep ownership clear before department heads maintain their role rates.
                    </div>
                </div>
                <Space size="small">
                    <Button
                        className="btn-white-add"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        variant="secondary"
                        size="small"
                    >
                        ADD DEPARTMENT
                    </Button>

                    <Input
                        placeholder="Search"
                        style={{ minWidth: 250 }}
                        prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                        suffix={
                            search ? (
                                <CloseCircleOutlined
                                    style={{
                                        color: "rgba(0,0,0,.25)",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setSearch("")}
                                />
                            ) : null
                        }
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </Space>
            </div>

            {renderDepartmentSetup()}

            {/* Table */}
            <Table
                columns={columns}
                dataSource={departments}
                loading={loading}
                rowKey="id"
                pagination={pagination}
                onChange={handleTableChange}
                variant="bordered"
            />

            {/* Modal */}
            {modalVisible && (
                <DepartmentFormModal
                    visible={modalVisible}
                    department={editingDepartment}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default DepartmentList;
